import React from "react";
import {
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from "@material-ui/core";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import * as customColors from "../../../../../theme/customColors";
import HaklaiSectionLabelCell from "../../shared/ui/HaklaiSectionLabelCell";
import {
  formatHaklaiClassicMoneyInput,
  formatHaklaiWholeMoney,
  normalizeHaklaiClassicMoneyInputOnBlur
} from "../../shared/haklaiMoneyHelpers";
import {
  HaklaiStyledCell as StyledCell,
  HaklaiStyledNameCell as StyledNameCell,
  joinClassNames,
  buildFieldErrorId,
  FieldShell
} from "../../shared/table/haklaiTableShared";
import {
  DEPRECIATION_COLUMN_WIDTH,
  PROPERTY_PART_NAME_COLUMN_WIDTH,
  SALE_VALUE_PLACEHOLDER
} from "../haklaiPropertyPartsStyles";
import {
  CATEGORIES
} from "../../shared/haklaiConstants";
import { getHaklaiZabanInputValue } from "../../shared/haklaiFlowHelpers";
import {
  HAKLAI_DEPRECIATION_LABELS,
  HAKLAI_DEPRECIATION_TOOLTIPS
} from "../../shared/haklaiDepreciationTexts";
import {
  HAKLAI_PROPERTY_PART_LABELS,
  HAKLAI_PROPERTY_PART_TOOLTIPS
} from "../haklaiPropertyPartsTexts";

const renderMutedCellValue = (classes, tooltipTitle, label = "—") => (
  <Tooltip title={tooltipTitle}>
    <Typography component="span" className={classes.nonRelevantValue}>
      {label}
    </Typography>
  </Tooltip>
);

const renderQuietFieldPlaceholder = (
  classes,
  shellClassName = "",
  label = "—",
  showCurrency = false,
  tooltipTitle = ""
) => {
  const placeholder = (
    <FieldShell
      classes={classes}
      shellClassName={shellClassName}
      showCurrency={showCurrency}
      currencyHidden={showCurrency}
    >
      <Box className={classes.fieldShellPlaceholder}>
        <Typography component="span" className={classes.nonRelevantValue}>
          {label}
        </Typography>
      </Box>
    </FieldShell>
  );

  return tooltipTitle ? (
    <Tooltip title={tooltipTitle}>
      <Box>{placeholder}</Box>
    </Tooltip>
  ) : placeholder;
};

const hasDisplayValue = (value) => (
  value !== null
  && value !== undefined
  && value !== ""
);

const renderReadOnlyMoneyField = (classes, {
  name,
  value,
  shellClassName = classes.readOnlyMoneyFieldShell,
  className = classes.readOnlyField,
  inputProps
}) => (
  <FieldShell
    classes={classes}
    shellClassName={shellClassName}
    showCurrency
  >
    <TextField
      name={name}
      type="text"
      variant="outlined"
      size="small"
      disabled
      className={className}
      value={value}
      InputProps={{
        readOnly: true,
        inputProps
      }}
    />
  </FieldShell>
);

const renderReadOnlyTextField = (classes, {
  name,
  value,
  shellClassName = classes.readOnlyMoneyFieldShell,
  className = classes.readOnlyFieldWide,
  showCurrency = false,
  currencyHidden = false,
  inputProps
}) => (
  <FieldShell
    classes={classes}
    shellClassName={shellClassName}
    showCurrency={showCurrency}
    currencyHidden={currencyHidden}
  >
    <TextField
      name={name}
      type="text"
      variant="outlined"
      size="small"
      disabled
      className={className}
      value={value}
      InputProps={{
        readOnly: true,
        inputProps
      }}
    />
  </FieldShell>
);

export const HaklaiPropertyPartRow = React.memo(({
  classes,
  rowKey,
  part,
  hasZaban,
  ptorType,
  ptorOptions = [],
  canRemove = true,
  subSectionLabel,
  subSectionRowSpan,
  isFirstInSubSection,
  rowValidationState,
  showZabanColumn,
  showPre1997ConstructionQuestion,
  showAddButton = false,
  nameColumnWidth = PROPERTY_PART_NAME_COLUMN_WIDTH,
  sectionLabel = "",
  addMode,
  addAriaLabel = "",
  depreciationState = {},
  onOpenDepreciation,
  onRentalDepreciationToggle,
  onAddClick,
  onFieldChange,
  onFieldBlur,
  onRemove,
  setValueInputRef,
  setBuiltValueInputRef,
  setRowRef,
  cellErrors = {},
  isFlashing = false
}) => {
  const isResidential = part.category === CATEGORIES.RESIDENTIAL;
  const isFarmRemainder = part.category === CATEGORIES.FARM_REMAINDER;
  const showRentalDepreciationControl = depreciationState.isResidentialAutoAllowed === true;
  const isRentalDepreciationChecked = (
    depreciationState.hasRentalSelected === true
    || depreciationState.hasRentalConfigured === true
  );
  const purchaseDepreciationError = cellErrors.purchaseDepreciation || "";
  const purchaseDepreciationErrorId = buildFieldErrorId(rowKey, "purchaseDepreciation");
  const hasPurchaseDepreciationError = Boolean(purchaseDepreciationError);
  const rentalDepreciationDisabled = !depreciationState.isRelevant;
  const rentalEditDisabled = rentalDepreciationDisabled || !isRentalDepreciationChecked;
  const rentalQuestionTooltip = rentalDepreciationDisabled
    ? HAKLAI_DEPRECIATION_TOOLTIPS.rentalNotRelevant
    : isRentalDepreciationChecked
      ? HAKLAI_DEPRECIATION_TOOLTIPS.removeRentalDepreciation
      : HAKLAI_DEPRECIATION_TOOLTIPS.enterRentalDepreciation;
  const rentalEditTooltip = rentalEditDisabled
    ? HAKLAI_DEPRECIATION_TOOLTIPS.mustMarkRentedBeforeEdit
    : depreciationState.hasInvalid
      ? purchaseDepreciationError
        || depreciationState.validationError
        || HAKLAI_DEPRECIATION_TOOLTIPS.rentalDepreciationNeedsReview
      : HAKLAI_DEPRECIATION_TOOLTIPS.editRentalDepreciation;
  const hasLabelError = Boolean(cellErrors.label);
  const hasValueError = Boolean(cellErrors.value);
  const hasZabanAmountError = Boolean(cellErrors.zabanAmount);
  const zabanInputValue = getHaklaiZabanInputValue(part, hasZaban);
  const farmRemainderDisplayStatus = part.displayStatus || "";

  return (
    <TableRow
      key={rowKey}
      ref={setRowRef ? setRowRef(rowKey) : undefined}
      className={isFlashing ? classes.rowFlash : undefined}
    >
      <HaklaiSectionLabelCell
        title={sectionLabel}
        addButton={sectionLabel && showAddButton ? {
          classes,
          mode: addMode,
          onClick: onAddClick,
          label: addAriaLabel
        } : null}
      />
      {isFirstInSubSection && subSectionLabel ? (
        <StyledCell
          rowSpan={subSectionRowSpan}
          align="center"
          style={{
            width: nameColumnWidth,
            minWidth: nameColumnWidth,
            maxWidth: nameColumnWidth
          }}
          className={classes.subSectionCell}
        >
          <Box className={classes.subSectionCellInner}>
            <Typography variant="body2" className={classes.subSectionCellText}>
              {subSectionLabel}
            </Typography>
          </Box>
        </StyledCell>
      ) : null}

      <StyledNameCell
        align="right"
        style={{
          width: nameColumnWidth,
          minWidth: nameColumnWidth,
          maxWidth: nameColumnWidth
        }}
      >
        <Box
          className={[
            classes.nameFieldStack,
            classes.nameFieldStackCentered
          ].filter(Boolean).join(" ")}
        >
          <FieldShell
            classes={classes}
            shellClassName={classes.nameFieldShell}
            errorMsg={cellErrors.label || ""}
            errorId={buildFieldErrorId(rowKey, "label")}
          >
            <Tooltip title={part.label || ""} disableHoverListener={!part.label}>
              <TextField
                name={`${rowKey}_label_readonly`}
                variant="outlined"
                size="small"
                disabled
                className={[
                  classes.readOnlyNameInput,
                  hasLabelError ? classes.readOnlyNameInputError : ""
                ].filter(Boolean).join(" ")}
                value={part.label || ""}
                InputProps={{ readOnly: true }}
                inputProps={{
                  tabIndex: -1,
                  "aria-invalid": hasLabelError,
                  "aria-describedby": hasLabelError && cellErrors.label
                    ? buildFieldErrorId(rowKey, "label")
                    : undefined
                }}
              />
            </Tooltip>
          </FieldShell>
        </Box>
      </StyledNameCell>

      <StyledCell
        align="center"
        className={joinClassNames(!isResidential ? classes.nonRelevantCell : "")}
      >
        {!isResidential ? (
          renderMutedCellValue(classes, HAKLAI_PROPERTY_PART_LABELS.notRelevantInSection)
        ) : (
          <Box className={classes.ptorCellStack}>
            <FieldShell
              classes={classes}
              shellClassName={classes.ptorFieldShell}
            >
              <TextField
                select
                name={`${rowKey}_ptorType`}
                variant="outlined"
                size="small"
                className={classes.ptorSelect}
                value={ptorType || "none"}
                onChange={(event) => onFieldChange(rowKey, "ptorType", event.target.value)}
                SelectProps={{
                  MenuProps: {
                    anchorOrigin: { vertical: "bottom", horizontal: "right" },
                    transformOrigin: { vertical: "top", horizontal: "right" },
                    getContentAnchorEl: null,
                    PaperProps: { className: "haklai-select-menu" }
                  }
                }}
              >
                {ptorOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </FieldShell>
          </Box>
        )}
      </StyledCell>

      <StyledCell align="center">
        {isFarmRemainder ? (
          farmRemainderDisplayStatus === "zero" ? (
            renderReadOnlyTextField(classes, {
              name: `${rowKey}_value`,
              value: HAKLAI_PROPERTY_PART_LABELS.noFarmRemainder,
              shellClassName: classes.moneyFieldShell,
              showCurrency: true,
              currencyHidden: true
            })
          ) : farmRemainderDisplayStatus === "invalid" ? (
            renderQuietFieldPlaceholder(
              classes,
              classes.moneyFieldShell,
              "—",
              true,
              HAKLAI_PROPERTY_PART_TOOLTIPS.farmRemainderInvalid
            )
          ) : hasDisplayValue(part.value) ? (
            renderReadOnlyMoneyField(classes, {
              name: `${rowKey}_value`,
              value: formatHaklaiWholeMoney(part.value),
              shellClassName: classes.moneyFieldShell
            })
          ) : (
            renderQuietFieldPlaceholder(
              classes,
              classes.moneyFieldShell,
              "—",
              true
            )
          )
        ) : (
          <FieldShell
            classes={classes}
            shellClassName={classes.moneyFieldShell}
            errorMsg={cellErrors.value || ""}
            errorId={buildFieldErrorId(rowKey, "value")}
            showCurrency
          >
            <TextField
              name={`${rowKey}_value`}
              type="text"
              variant="outlined"
              size="small"
              className={[
                classes.numberInput,
                rowValidationState.isVisuallyDrafting ? classes.optionalTrailingField : ""
              ].filter(Boolean).join(" ")}
              value={formatHaklaiClassicMoneyInput(part.value)}
              placeholder={SALE_VALUE_PLACEHOLDER}
              inputRef={setValueInputRef(rowKey)}
              onChange={(event) => onFieldChange(rowKey, "value", event.target.value)}
              onBlur={() => {
                const normalizedValue = normalizeHaklaiClassicMoneyInputOnBlur(part.value);
                if (normalizedValue !== part.value) {
                  onFieldChange(rowKey, "value", normalizedValue);
                }
                onFieldBlur(rowKey, "value");
              }}
              error={hasValueError}
              inputProps={{
                "aria-invalid": hasValueError,
                "aria-describedby": hasValueError && cellErrors.value
                  ? buildFieldErrorId(rowKey, "value")
                  : undefined
              }}
            />
          </FieldShell>
        )}
      </StyledCell>

      {showZabanColumn ? (
        <StyledCell
          align="center"
          className={!isResidential ? classes.nonRelevantCell : undefined}
        >
          {isResidential ? (
            <Box className={classes.zabanDetailsStack}>
              <FieldShell
                classes={classes}
                shellClassName={classes.moneyFieldShell}
                errorMsg={cellErrors.zabanAmount || ""}
                errorId={buildFieldErrorId(rowKey, "zabanAmount")}
                showCurrency
              >
                <TextField
                  name={`${rowKey}_zabanAmount`}
                  type="text"
                  variant="outlined"
                  size="small"
                  className={[
                    classes.numberInput,
                    rowValidationState.isVisuallyDrafting ? classes.optionalTrailingField : ""
                  ].filter(Boolean).join(" ")}
                  value={formatHaklaiClassicMoneyInput(zabanInputValue)}
                  placeholder={HAKLAI_PROPERTY_PART_LABELS.zabanValuePlaceholder}
                  inputRef={setBuiltValueInputRef(rowKey)}
                  onChange={(event) => onFieldChange(rowKey, "zabanAmount", event.target.value)}
                  onBlur={() => {
                    const normalizedZabanAmount = normalizeHaklaiClassicMoneyInputOnBlur(zabanInputValue);
                    if (normalizedZabanAmount !== zabanInputValue) {
                      onFieldChange(rowKey, "zabanAmount", normalizedZabanAmount);
                    }
                    onFieldBlur(rowKey, "zabanAmount");
                  }}
                  error={hasZabanAmountError}
                  inputProps={{
                    "aria-invalid": hasZabanAmountError,
                    "aria-describedby": hasZabanAmountError && cellErrors.zabanAmount
                      ? buildFieldErrorId(rowKey, "zabanAmount")
                      : undefined
                  }}
                />
              </FieldShell>
              {showPre1997ConstructionQuestion ? (
                <Box className={classes.pre1997QuestionWrap}>
                  <Checkbox
                    color="primary"
                    size="small"
                    checked={part.constructionsDoneBeforeApril1997 === true}
                    onChange={(event) => onFieldChange(
                      rowKey,
                      "constructionsDoneBeforeApril1997",
                      event.target.checked
                    )}
                    className={classes.pre1997Checkbox}
                    inputProps={{
                      "aria-label": HAKLAI_PROPERTY_PART_LABELS.pre1997ConstructionQuestion
                    }}
                  />
                  <Typography component="span" className={classes.pre1997QuestionLabel}>
                    {HAKLAI_PROPERTY_PART_LABELS.pre1997ConstructionQuestion}
                  </Typography>
                </Box>
              ) : null}
            </Box>
          ) : (
            renderMutedCellValue(classes, HAKLAI_PROPERTY_PART_LABELS.notRelevantInSection)
          )}
        </StyledCell>
      ) : null}

      <StyledCell
        align="center"
        style={{ width: DEPRECIATION_COLUMN_WIDTH }}
      >
        <Box className={classes.depreciationFieldContent}>
          {showRentalDepreciationControl ? (
            <FieldShell
              classes={classes}
              shellClassName={classes.rentalDepreciationFieldShell}
              errorMsg={purchaseDepreciationError}
              errorId={purchaseDepreciationErrorId}
            >
              <Box className={classes.rentalDepreciationControl}>
                <Box className={classes.rentalDepreciationActionRow}>
                  <Tooltip title={rentalQuestionTooltip}>
                    <span>
                      <RadioGroup
                        row
                        name={`${rowKey}_rentalDepreciationEnabled`}
                        className={classes.rentalDepreciationRadioGroup}
                        value={isRentalDepreciationChecked ? "yes" : "no"}
                        aria-label={HAKLAI_DEPRECIATION_LABELS.rentedQuestion}
                        aria-invalid={hasPurchaseDepreciationError || undefined}
                        aria-describedby={hasPurchaseDepreciationError
                          ? purchaseDepreciationErrorId
                          : undefined}
                        onChange={(event) => onRentalDepreciationToggle?.(
                          rowKey,
                          part,
                          event.target.value === "yes",
                          depreciationState
                        )}
                      >
                        <FormControlLabel
                          value="no"
                          disabled={rentalDepreciationDisabled}
                          className={classes.rentalDepreciationRadioOption}
                          control={<Radio size="small" color="primary" className={classes.rentalDepreciationRadio} />}
                          label={HAKLAI_DEPRECIATION_LABELS.no}
                        />
                        <FormControlLabel
                          value="yes"
                          disabled={rentalDepreciationDisabled}
                          className={classes.rentalDepreciationRadioOption}
                          control={<Radio size="small" color="primary" className={classes.rentalDepreciationRadio} />}
                          label={HAKLAI_DEPRECIATION_LABELS.yes}
                        />
                      </RadioGroup>
                    </span>
                  </Tooltip>

                  <Tooltip title={rentalEditTooltip}>
                    <span>
                      <IconButton
                        size="small"
                        name={`${rowKey}_purchaseDepreciation`}
                        data-field={`${rowKey}_purchaseDepreciation`}
                        disabled={rentalEditDisabled}
                        aria-label={HAKLAI_DEPRECIATION_TOOLTIPS.editRentalDepreciation}
                        className={[
                          classes.rentalDepreciationEditButton,
                          isRentalDepreciationChecked ? classes.rentalDepreciationEditButtonActive : "",
                          depreciationState.hasInvalid ? classes.rentalDepreciationEditButtonWarning : "",
                          rentalEditDisabled ? classes.rentalDepreciationEditButtonDisabled : ""
                        ].filter(Boolean).join(" ")}
                        onClick={() => {
                          if (rentalEditDisabled) return;
                          onOpenDepreciation?.(rowKey, part, depreciationState);
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </Box>
            </FieldShell>
          ) : (
            renderQuietFieldPlaceholder(classes)
          )}
        </Box>
      </StyledCell>

      <StyledCell
        align="center"
        style={{ padding: 2 }}
        className={[classes.groupFrameTrailingActionCell, "haklai-icon-cell"].join(" ")}
      >
        <Box className={classes.rowActionCellContent}>
          {isFarmRemainder || !canRemove ? null : (
            <IconButton
              size="small"
              aria-label={HAKLAI_PROPERTY_PART_TOOLTIPS.deletePart}
              onClick={() => onRemove(rowKey)}
            >
              <DeleteOutlineIcon fontSize="small" style={{ color: customColors.red }} />
            </IconButton>
          )}
        </Box>
      </StyledCell>
    </TableRow>
  );
});
